import { useState } from "react";
import { SystemDocumentTabProps } from "./SystemDocumentsTab";
import { SystemDocument, SystemDocumentRowViewModel } from "./SystemDocuments.types";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { isNullOrWhitespace } from "../../../common/stringOperations";
import { useTranslation } from "../../../localization/useTranslation";
import { useDotLegalFileUploadDataMapping } from "../../../common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks";
import { EntityType } from "../../../common/Common.types";
import { useStateUrlParams } from "../../../common/hooks/useStateUrlParams.ts";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { useBestPracticeDialogsHook } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.hooks.ts";
import { getAiQualityScanOverflowMenuItem } from "@common/components/bestPracticeCheckDialog/aiQualityScanOverflowMenuItem.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { getUniqueFilterOptions, sortByName } from "@common/hooks/filterUtils.ts";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, matchesDateFilter, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { useAiFeatureAccess } from "../../../plan/aiFeatureGate/useAiFeatureAccess";

import { useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";

export function useSystemDocuments(props: SystemDocumentTabProps) {
    const queryClient = useDotLegalQueryClient();

    const [selectedDocument, setSelectedDocument] = useState<SystemDocument>();
    const [showDocumentDialog, setShowDocumentDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showAuditLogDialog, setShowAuditLogDialog] = useStateUrlParams("document-auditlog", null);

    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const { showAiQualityAssurance, planActivated } = useAiFeatureAccess();
    const {
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckDocumentToQualityAssurance,
        setBestPracticeCheckDocumentToQualityAssurance,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
        onCheckComplete,
    } = useBestPracticeDialogsHook();
    const { downloadDocumentVersion } = useDotLegalFileUploadDataMapping();
    const { translateString } = useTranslation();
    const [downloadingDocument, setDownloadingDocument] = useState<string | undefined>(undefined);

    const { selectFilters, booleanFilters, dateFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `system-${props.systemId}-documents-filters`
    );
    const createdFilter = dateFilters.created ?? null;
    const qualityScanFilter = booleanFilters.qualityScan ?? "";

    const url = `/system/${props.systemId}/Documents`;
    const { isLoading, data: rawData } = useDotLegalQuery<Array<SystemDocumentRowViewModel>>(url);

    const data = applyClientFilters(
        rawData,
        [
            { id: "name", candidate: (doc) => doc.name },
            { id: "document", candidate: (doc) => doc.fileName ?? doc.link ?? "" },
        ],
        selectFilters,
        {},
        filterOperators,
        (doc) => {
            if (!matchesDateFilter(createdFilter, doc.created)) return false;

            if (qualityScanFilter !== "") {
                const hasResult = doc.aiQualityAssuranceResult != null;
                if (qualityScanFilter && !hasResult) return false;
                if (!qualityScanFilter && hasResult) return false;
            }

            return true;
        }
    );

    function refetchDocuments() {
        queryClient.refetchQueries(url);
        props.refetch();
    }

    async function onDownloadClick(documentId: string, dataId: string, fileName: string, link: string | undefined) {
        if (!isNullOrWhitespace(link)) {
            window.open(link, "_blank");
        } else {
            setDownloadingDocument(documentId);
            await downloadDocumentVersion(documentId, dataId, fileName, EntityType.System, props.systemId!);
            setDownloadingDocument(undefined);
        }
    }

    function getMenuItems(document: SystemDocumentRowViewModel) {
        let menuItems = [];

        if (permissions.systemPermissions.edit) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setSelectedDocument({
                        documentId: document.documentId,
                        name: document.name,
                        fileName: "",
                        link: document.link ?? "",
                    });
                    setShowDocumentDialog(true);
                },
            });
        }

        if (isNullOrWhitespace(document.fileName)) {
            menuItems.push({
                menuItem: translateString("openLink"),
                onClick: () => window.open(document.link!, "_blank"),
            });
        } else {
            menuItems.push({
                menuItem: translateString("download"),
                onClick: () => downloadDocumentVersion(document.documentId, document.dataId!, document.fileName!, EntityType.System, props.systemId!),
            });
        }

        if (permissions.systemPermissions.delete) {
            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setSelectedDocument({
                        documentId: document.documentId,
                        name: document.name,
                        fileName: "",
                        link: document.link ?? "",
                    });
                    setShowDeleteDialog(true);
                },
            });
        }

        if (permissions.canAccessAuditLog) {
            menuItems.push({
                menuItem: translateString("auditlog"),
                onClick: () => {
                    setShowAuditLogDialog(document.systemDocumentId);
                },
                planMenuItemInfo: {
                    hasAccess: platformFeatures.auditLog,
                    menuName: translateString("auditlog"),
                    tooltip: translateString("upgradePrivacyPlanToday"),
                },
            });
        }

        if (showAiQualityAssurance && !isNullOrWhitespace(document.fileName)) {
            menuItems.push(
                getAiQualityScanOverflowMenuItem(
                    () => {
                        if (document.aiQualityAssuranceResult) {
                            setBestPracticeCheckDocument({ documentId: document.documentId });
                        } else {
                            setBestPracticeCheckDocumentToQualityAssurance({ documentId: document.documentId });
                        }
                    },
                    planActivated,
                    document.isTooLargeForAi,
                    translateString
                )
            );
        }

        return menuItems;
    }

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "name",
                    label: translateString("name"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.name ?? [],
                    fixedData: getUniqueFilterOptions(rawData, (d) => d.name).sort(sortByName),
                },
                {
                    id: "document",
                    label: translateString("document"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.document ?? [],
                    fixedData: getUniqueFilterOptions(rawData, (d) => d.fileName ?? d.link ?? undefined).sort(sortByName),
                },
                {
                    id: "created",
                    label: translateString("created"),
                    type: FilterOptionType.DateField,
                    value: createdFilter ?? "",
                    hideNoDate: true,
                },
                {
                    id: "qualityScan",
                    label: translateString("aiQualityScan"),
                    type: FilterOptionType.Boolean,
                    value: qualityScanFilter,
                },
            ],
            filterOperators
        );
    }

    const hasActiveFilters = Object.values(selectFilters).some((v) => v.length > 0) || createdFilter !== null || qualityScanFilter !== "";

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        isLoading,
        data,
        hasDocuments: isLoading || (rawData ?? []).length > 0,
        refetchDocuments,
        getMenuItems,
        selectedDocument,
        setSelectedDocument,
        showDocumentDialog,
        setShowDocumentDialog,
        showDeleteDialog,
        setShowDeleteDialog,
        showAuditLogDialog,
        setShowAuditLogDialog,
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckDocumentToQualityAssurance,
        setBestPracticeCheckDocumentToQualityAssurance,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
        onCheckComplete,
        downloadingDocument,
        onDownloadClick,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
    };
}
